import { useEffect, useRef } from 'react';
import {
  AuthType,
  init,
  AppEmbed as TSAppEmbed,
  EmbedEvent,
  Action,
  Page,
} from '@thoughtspot/visual-embed-sdk';
import BEM from 'common/bem';

const classes = BEM.with('ClinicalWorkflows');

const LIVEBOARD_PATH_PREFIXES = [
  '/liveboard/',
  '/pinboard/',
  '/insights/liveboard/',
  '/insights/pinboard/',
];

type Props = {
  host: string;
  token: string;
  orgId: number;
  onLiveboardSelect: (guid: string) => void;
};

export default function AppEmbedComponent({ host, token, orgId, onLiveboardSelect }: Props) {
  const containerRef = useRef<HTMLDivElement>(null);
  const initializedHostRef = useRef<string | null>(null);
  const tokenRef = useRef(token);
  tokenRef.current = token;
  const onLiveboardSelectRef = useRef(onLiveboardSelect);
  onLiveboardSelectRef.current = onLiveboardSelect;

  useEffect(() => {
    if (!containerRef.current) return;

    if (initializedHostRef.current !== host) {
      init({
        thoughtSpotHost: host,
        authType: AuthType.TrustedAuthTokenCookieless,
        getAuthToken: () => Promise.resolve(tokenRef.current),
        customizations: {
          style: {
            customCSS: {
              // rules_UNSTABLE targets ThoughtSpot internal selectors.
              // Class names sourced from the live embed DOM — may need updates on TS upgrades.
              rules_UNSTABLE: {
                // Hide bulk-selection action bar buttons not covered by hiddenActions enum
                '[data-testid="add-to-collection-action"]': {
                  display: 'none !important',
                },
                // Hide the per-row "..." context menu button
                '[data-testid="action-menu-button"]': {
                  display: 'none !important',
                },
                // Hide the data source/model name chip in the Spotter search bar
                '[data-testid="conv-assist-data-source-selection-btn"]': {
                  display: 'none !important',
                },
                // Hide "Make this Liveboard discoverable" checkbox in create dialog
                '[data-testid="create-pinboard-checkbox"]': {
                  display: 'none !important',
                },
                // Hide the info button next to the discoverable checkbox
                '[data-testid="create-liveboard-info-button"]': {
                  display: 'none !important',
                },
              },
            },
          },
          content: {
            strings: {
              Spotter: 'Ask Tiger',
            },
          },
          iconSpriteUrl: (() => {
            try {
              const url = new URL('thoughtspot-icons.svg', window.location.href.split('#')[0]);
              return url.protocol === 'https:' ? url.href : undefined;
            } catch {
              return undefined;
            }
          })(),
        },
      });
      initializedHostRef.current = host;
    }

    const embed = new TSAppEmbed(containerRef.current, {
      pageId: Page.Liveboards,
      overrideOrgId: orgId,
      showPrimaryNavbar: false,
      disableProfileAndHelp: true,
      hiddenActions: [
        Action.Share,
        Action.ShowUnderlyingData,
        Action.SpotIQAnalyze,
        Action.Explore,
        Action.SyncToOtherApps,
        Action.SyncToSheets,
        Action.ManagePipelines,
        Action.CopyLink,
        Action.ExportTML,
        Action.RequestVerification,
        Action.LiveboardInfo,
        Action.SyncToSlack,
        Action.SyncToTeams,
        Action.TML,
        Action.MarkAsVerified,
        Action.RequestAccess,
        Action.AIHighlights,
        Action.PreviewDataSpotter,
        Action.ChooseDataSources,
      ],
    });

    embed.on(EmbedEvent.RouteChange, (payload: { data: { currentPath: string } }) => {
      const { currentPath } = payload.data;
      const matchedPrefix = LIVEBOARD_PATH_PREFIXES.find((prefix) =>
        currentPath.startsWith(prefix)
      );
      if (!matchedPrefix) return;

      const guid = currentPath.slice(matchedPrefix.length).split('/')[0];
      if (!guid) return;

      onLiveboardSelectRef.current(guid);
    });

    embed.render();

    return () => {
      embed.destroy();
    };
  }, [host, orgId]);

  return <div ref={containerRef} className={classes('liveboard-embed')} />;
}
